import { earlyAccessRedeemResponseSchema } from '@koderek/shared';
import { Alert, Button, Group, Loader, Stack, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { api } from '../api/client';
import { ErrorAlert } from '../components/ErrorAlert';
import { AuthCard } from '../features/auth/AuthCard';
import { useMe } from '../features/auth/session';
import { t } from '../texts/pl';

/** US-409: tajny link wczesnego dostępu. */
export function EarlyAccessPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const token = params.get('token') ?? '';
  const me = useMe();

  if (!token) {
    return (
      <AuthCard title={t.earlyAccess.title}>
        <Alert color="red">{t.earlyAccess.missingToken}</Alert>
      </AuthCard>
    );
  }
  if (me.isPending) return <Loader aria-label="Ładowanie" />;
  if (!me.data) {
    // AC-2: po logowaniu wracamy tu; rejestracja zabiera token ze sobą (dostęp trafia do konta od razu).
    const here = encodeURIComponent(location.pathname + location.search);
    return (
      <AuthCard title={t.earlyAccess.title} intro={t.earlyAccess.loginIntro}>
        <Group>
          <Button component={Link} to={`/logowanie?powrot=${here}`}>
            {t.nav.login}
          </Button>
          <Button component={Link} to={`/rejestracja?wczesny=${encodeURIComponent(token)}`} variant="light">
            {t.nav.register}
          </Button>
        </Group>
      </AuthCard>
    );
  }
  return <Redeem token={token} />;
}

function Redeem({ token }: { token: string }) {
  const redeem = useMutation({
    mutationFn: () => api('POST', '/editions/early-access', { body: { token }, schema: earlyAccessRedeemResponseSchema }),
  });
  const { mutate } = redeem;
  useEffect(() => mutate(), [mutate]);

  return (
    <AuthCard title={t.earlyAccess.title}>
      {redeem.isPending || redeem.isIdle ? (
        <Text role="status">
          <Loader size="xs" mr="xs" />
          {t.earlyAccess.working}
        </Text>
      ) : null}
      {redeem.isError ? <ErrorAlert error={redeem.error} /> : null}
      {redeem.data ? (
        <Stack gap="sm">
          <Alert color="green" role="status">
            {redeem.data.result === 'granted'
              ? t.earlyAccess.granted(redeem.data.name)
              : t.earlyAccess.alreadyOpen(redeem.data.name)}
          </Alert>
          <Button component={Link} to={`/edycje/${redeem.data.slug}`}>
            {t.earlyAccess.toRegistration}
          </Button>
        </Stack>
      ) : null}
    </AuthCard>
  );
}
